import { useCallback, type MouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { landingPathToSectionId, LANDING_HERO_SECTION_ID, scrollToLandingSection } from '../lib/landingScroll.js';

export function useLandingSectionNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const navigateToLandingSection = useCallback(
    (path: string) => {
      const sectionId = landingPathToSectionId(path);
      if (!sectionId) {
        navigate(path);
        return;
      }

      if (pathname === '/') {
        scrollToLandingSection(sectionId);
        window.history.replaceState(null, '', `#${sectionId}`);
        return;
      }

      if (document.getElementById(sectionId)) {
        scrollToLandingSection(sectionId);
        window.history.replaceState(null, '', `${path}#${sectionId}`);
        return;
      }

      navigate({ pathname: '/', hash: sectionId });
    },
    [navigate, pathname],
  );

  const handleLandingSectionClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, path: string) => {
      const sectionId = landingPathToSectionId(path);
      if (!sectionId) return;
      event.preventDefault();
      navigateToLandingSection(path);
    },
    [navigateToLandingSection],
  );

  const handleLandingLogoClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      if (pathname === '/') {
        scrollToLandingSection(LANDING_HERO_SECTION_ID);
        window.history.replaceState(null, '', '/');
        return;
      }

      navigate({ pathname: '/', hash: LANDING_HERO_SECTION_ID });
    },
    [navigate, pathname],
  );

  return { handleLandingSectionClick, handleLandingLogoClick, navigateToLandingSection };
}
